import React from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { inr, type ID, type SalaryBreakdown } from '@hrms/shared';
import { api } from '../../../lib/api';
import { openFile } from '../../../lib/files';
import { Alert, Button, Card, DataTable, Divider, ErrorState, Loading, Modal, Row, Stack, Text, useTheme } from '../../../ui';
import { AmountLine, KeyValue, TYPE_LABEL } from './common';

/** The preview endpoint also returns calculator warnings (e.g. CTC too low for the structure). */
export type BreakdownWithWarnings = SalaryBreakdown & { warnings?: string[] };

const GROUPS = ['earning', 'reimbursement', 'employer_contribution', 'deduction'] as const;

/** CTC → components breakdown with headline numbers and calculator warnings. */
export function BreakdownTable({ b, testID }: { b: BreakdownWithWarnings; testID?: string }) {
  const { colors } = useTheme();
  const lines = GROUPS.flatMap((g) => b.lines.filter((l) => l.type === g));
  return (
    <Stack gap={12} testID={testID}>
      {!!b.warnings?.length && <Alert tone="warning" title="Check this structure">{b.warnings}</Alert>}
      <Row gap={24}>
        <KeyValue label="Annual CTC" value={inr(b.ctc)} />
        <KeyValue label="Gross / month" value={inr(b.gross_monthly)} />
        <KeyValue label="Est. take-home / month" value={inr(b.net_monthly_estimate)} color={colors.success} />
      </Row>
      <DataTable
        rows={lines}
        keyExtractor={(l) => l.code}
        columns={[
          { key: 'name', title: 'Component', width: 180, flex: 2, sortable: false, render: (l) => <Text>{l.name} <Text variant="small">({l.code})</Text></Text> },
          { key: 'type', title: 'Type', width: 150, sortable: false, render: (l) => TYPE_LABEL[l.type] ?? l.type },
          { key: 'monthly', title: 'Monthly', width: 110, align: 'right', sortable: false, render: (l) => inr(l.monthly) },
          { key: 'annual', title: 'Annual', width: 120, align: 'right', sortable: false, render: (l) => inr(l.annual) },
        ]}
      />
    </Stack>
  );
}

/** Sheet with a payslip's lines grouped by type. Loads the full payslip by id. */
export function PayslipSheet({ id, title, onClose }: { id: ID | null; title?: string; onClose: () => void }) {
  const { colors } = useTheme();
  const q = useQuery({ queryKey: ['payslip', id], queryFn: () => api.payroll.payslip(id!), enabled: !!id });
  const p = q.data;
  return (
    <Modal
      open={!!id}
      onClose={onClose}
      title={title ?? p?.employee?.full_name ?? 'Payslip'}
      subtitle={p ? `${p.payable_days}/${p.days_in_month} paid days${p.lop_days ? ` · ${p.lop_days} LOP` : ''}` : undefined}
      width={560}
      testID="payslip-sheet"
      footer={
        <>
          <Button title="Close" onPress={onClose} />
          {!!id && <Button variant="primary" icon="eye" title="Open payslip" onPress={() => void openFile(api.payroll.payslipHtmlPath(id))} />}
        </>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : q.isError || !p ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <Stack gap={12}>
          {p.variance_flag && (
            <Alert tone="warning">
              {`Net pay changed ${p.variance_pct?.toFixed(1)}% vs last month (${inr(p.previous_net)}).`}
            </Alert>
          )}
          {GROUPS.map((t) => {
            const lines = (p.lines ?? []).filter((l) => l.type === t);
            if (!lines.length) return null;
            return (
              <Card key={t} title={TYPE_LABEL[t]}>
                <View>
                  {lines.map((l) => (
                    <AmountLine key={l.code} label={l.name} value={inr(l.amount)} />
                  ))}
                </View>
              </Card>
            );
          })}
          <Divider />
          <AmountLine label="Gross" value={inr(p.gross)} />
          <AmountLine label="Total deductions" value={inr(p.total_deductions)} />
          <AmountLine label="TDS" value={inr(p.tds)} />
          <AmountLine label="Net pay" value={inr(p.net_pay)} strong color={colors.success} />
          <AmountLine label="Employer cost" value={inr(p.employer_cost)} />
        </Stack>
      )}
    </Modal>
  );
}
